import { createApp } from "vue"

import "./styles/main.scss"

import App from "./App.vue"
import VueGtag from 'vue-gtag'
import gtagOptions from './plugins/gtag-options'
import router from './plugins/router'
import { configureVuetify } from './plugins/vuetify'
import { vuetifyRulesOptions } from './plugins/vuetify-rules'
import { createRulesPlugin } from 'vuetify/labs/rules'
import vuex from "./plugins/vuex"
import * as Sentry from "@sentry/vue"
// @ts-ignore: suppress "Could not find a declaration file for module"
import { PromiseDialog } from "vue3-promise-dialog"

import axiosInterceptors from "./services/auth-interceptor"
import i18n from "./plugins/i18n"

// components to make globally available
// @ts-ignore: suppress "Could not find a declaration file for module"
import ActionGroup from './components/ActionGroup.vue'
import HelpIcon from './components/HelpIcon.vue'
// @ts-ignore: suppress "Could not find a declaration file for module"
import Modal from './components/Modal.vue'
// @ts-ignore: suppress "Could not find a declaration file for module"
import OAutocomplete from './components/forms/OAutocomplete.vue'
// @ts-ignore: suppress "Could not find a declaration file for module"
import ORadioGroup from './components/forms/ORadioGroup.vue'
// @ts-ignore: suppress "Could not find a declaration file for module"
import OReadonly from './components/forms/OReadonly.vue'
// @ts-ignore: suppress "Could not find a declaration file for module"
import OSelect from './components/forms/OSelect.vue'
// @ts-ignore: suppress "Could not find a declaration file for module"
import OSwitch from './components/forms/OSwitch.vue'
// @ts-ignore: suppress "Could not find a declaration file for module"
import OTextField from './components/forms/OTextField.vue'
// @ts-ignore: suppress "Could not find a declaration file for module"
import TableFilter from './components/TableFilter.vue'
// @ts-ignore: suppress "Could not find a declaration file for module"
import TableTools from './components/TableTools.vue'
import RecordId from './components/RecordId.vue'

// channels
import ChannelRegistry from './channels/ChannelRegistry'
import AirportParkingReservations from './channels/AirportParkingReservations'
import CheapAirportParking from './channels/CheapAirportParking'
import Dock from './channels/Dock'
import Flash from './channels/Flash'
import GlobalAirportParking from './channels/GlobalAirportParking'
import Honk from './channels/Honk'
import NetPark from './channels/NetPark'
import OcraDirect from './channels/OcraDirect'
import OnAirParking from './channels/OnAirParking'
import ParkFast from './channels/ParkFast'
import ParkMobile from './channels/ParkMobile'
import ParkNFly from './channels/ParkNFly'
import ParkPlus from './channels/ParkPlus'
import ParkRoll from './channels/ParkRoll'
import ParkWhiz from './channels/ParkWhiz'
import PurchasePark2Fly from './channels/PurchasePark2Fly'
import RealTimeReservation from './channels/RealTimeReservation'
import ReservationsLab from './channels/ReservationsLab'
import Rightway from './channels/Rightway'
import SnagASpace from './channels/SnagASpace'
import SpotHero from './channels/SpotHero'
import T2 from './channels/T2'
import Way from './channels/Way'

// product types
import ProductTypeRegistry from './product_types/ProductTypeRegistry'
import GarageGroundFloor from './product_types/GarageGroundFloor'
import SelfCovered from './product_types/SelfCovered'
import SelfCoveredOversized from './product_types/SelfCoveredOversized'
import SelfCurbside from './product_types/SelfCurbside'
import SelfCurbsideOversized from './product_types/SelfCurbsideOversized'
import SelfIndoor from './product_types/SelfIndoor'
import SelfIndoorOversized from './product_types/SelfIndoorOversized'
import SelfRooftop from './product_types/SelfRooftop'
import SelfRooftopOversized from './product_types/SelfRooftopOversized'
import SelfUncovered from './product_types/SelfUncovered'
import SelfUncoveredOversized from './product_types/SelfUncoveredOversized'
import ValetCovered from './product_types/ValetCovered'
import ValetCoveredOversized from './product_types/ValetCoveredOversized'
import ValetCurbside from './product_types/ValetCurbside'
import ValetCurbsideOversized from './product_types/ValetCurbsideOversized'
import ValetIndoor from './product_types/ValetIndoor'
import ValetIndoorOversized from './product_types/ValetIndoorOversized'
import ValetRooftop from './product_types/ValetRooftop'
import ValetRooftopOversized from './product_types/ValetRooftopOversized'
import ValetUncovered from './product_types/ValetUncovered'
import ValetUncoveredOversized from './product_types/ValetUncoveredOversized'

// verticals
import VerticalRegistry from './verticals/VerticalRegistry'
import Airport from './verticals/Airport'
import Event from './verticals/Event'
import Monthly from './verticals/Monthly'
import OnDemand from './verticals/OnDemand'
import Parcs from './verticals/Parcs'
import SpotHeroIQ from './verticals/SpotHeroIQ'
import SpotHeroInOut from './verticals/SpotHeroInOut'
import Transient from './verticals/Transient'

const app = createApp(App)

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENV,
  sendDefaultPii: true,
  enableLogs: true, // https://docs.sentry.io/platforms/javascript/guides/vue/logs/
  integrations: [
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  // Session Replay
  // https://docs.sentry.io/platforms/javascript/guides/vue/session-replay/
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 1.0,
})

axiosInterceptors()

import PermissionsService from './services/PermissionsService'

// make the $can() function available to all components
app.provide("$can", function (permission: string, ...params: any[]) {
  return PermissionsService.can(permission, ...params)
})

// TODO: Here's the proper way to type provided values:
// https://vuejs.org/guide/typescript/composition-api#typing-provide-inject
// However, this would break calls to inject() in non-TypeScript files - so we can't do this until all components
// which use this injectable have been converted to TypeScript. For now, we'll just export a type that components
// can apply to the injected value.
export type Can = (permission: string, ...params: any[]) => boolean

app.use(router)
app.use(vuex)
app.use(i18n)
app.use(PromiseDialog)

// Google Analytics
app.use(VueGtag, gtagOptions, router)

// Make these components available globally, so we don't have to manually
// import them for every component.
app
  .component('ActionGroup', ActionGroup)
  .component('HelpIcon', HelpIcon)
  .component('Modal', Modal)
  .component('OAutocomplete', OAutocomplete)
  .component('ORadioGroup', ORadioGroup)
  .component('OReadonly', OReadonly)
  .component('OSelect', OSelect)
  .component('OSwitch', OSwitch)
  .component('OTextField', OTextField)
  .component('RecordId', RecordId)
  .component('TableFilter', TableFilter)
  .component('TableTools', TableTools)

// register channels
ChannelRegistry.register(new AirportParkingReservations())
ChannelRegistry.register(new CheapAirportParking())
ChannelRegistry.register(new Dock())
ChannelRegistry.register(new Flash())
ChannelRegistry.register(new GlobalAirportParking())
ChannelRegistry.register(new Honk())
ChannelRegistry.register(new NetPark())
ChannelRegistry.register(new OcraDirect())
ChannelRegistry.register(new OnAirParking())
ChannelRegistry.register(new ParkFast())
ChannelRegistry.register(new ParkMobile())
ChannelRegistry.register(new ParkPlus())
ChannelRegistry.register(new ParkNFly())
ChannelRegistry.register(new ParkRoll())
ChannelRegistry.register(new ParkWhiz())
ChannelRegistry.register(new PurchasePark2Fly())
ChannelRegistry.register(new RealTimeReservation())
ChannelRegistry.register(new ReservationsLab())
ChannelRegistry.register(new Rightway())
ChannelRegistry.register(new SnagASpace())
ChannelRegistry.register(new SpotHero())
ChannelRegistry.register(new T2())
ChannelRegistry.register(new Way())

/**
 * Initialize and populate the ProductTypeRegistry
 */

ProductTypeRegistry.register(new GarageGroundFloor())
ProductTypeRegistry.register(new SelfCovered())
ProductTypeRegistry.register(new SelfCoveredOversized())
ProductTypeRegistry.register(new SelfCurbside())
ProductTypeRegistry.register(new SelfCurbsideOversized())
ProductTypeRegistry.register(new SelfIndoor())
ProductTypeRegistry.register(new SelfIndoorOversized())
ProductTypeRegistry.register(new SelfRooftop())
ProductTypeRegistry.register(new SelfRooftopOversized())
ProductTypeRegistry.register(new SelfUncovered())
ProductTypeRegistry.register(new SelfUncoveredOversized())
ProductTypeRegistry.register(new ValetCovered())
ProductTypeRegistry.register(new ValetCoveredOversized())
ProductTypeRegistry.register(new ValetCurbside())
ProductTypeRegistry.register(new ValetCurbsideOversized())
ProductTypeRegistry.register(new ValetIndoor())
ProductTypeRegistry.register(new ValetIndoorOversized())
ProductTypeRegistry.register(new ValetRooftop())
ProductTypeRegistry.register(new ValetRooftopOversized())
ProductTypeRegistry.register(new ValetUncovered())
ProductTypeRegistry.register(new ValetUncoveredOversized())

VerticalRegistry.register(new Airport())
VerticalRegistry.register(new Event())
VerticalRegistry.register(new Monthly())
VerticalRegistry.register(new Parcs())
VerticalRegistry.register(new Transient())
VerticalRegistry.register(new SpotHeroInOut())
VerticalRegistry.register(new SpotHeroIQ())
VerticalRegistry.register(new OnDemand())

/**
 * Add channel and vertical colors to each Vuetify theme's color palette
 */

const globalColors: {[key: string]: string} = {}

ChannelRegistry.get().forEach(channel => {
  globalColors[`channel-${channel.name}`] = channel.color
})

VerticalRegistry.getMany().forEach(vertical => {
  globalColors[`vertical-${vertical.name}`] = vertical.color
})

const vuetify = configureVuetify(globalColors)
app.use(vuetify)

const vuetifyRules = createRulesPlugin(vuetifyRulesOptions, vuetify.locale)
app.use(vuetifyRules)

app.mount('#app')
